import { useEffect, useRef, RefObject } from 'react'
import { useRouter } from 'next/router'

const KEY_PREFIX = 'ecbo_scroll_el_'

// Destination URL (with locale prefix) of the most recent popstate navigation.
// null = last navigation was a link/push, not back/forward.
// Persists across component unmount/remount since it's module-level.
let pendingPopStatePath: string | null = null

if (typeof window !== 'undefined') {
  // window.location is already updated to the destination when popstate fires.
  window.addEventListener('popstate', () => {
    pendingPopStatePath = window.location.pathname + window.location.search
  })
}

// Strip locale prefix so the path matches router.asPath (which has no locale).
// Handles: /en/bookings → /bookings, /zh-TW/bookings → /bookings
const stripLocalePrefix = (path: string): string =>
  path.replace(/^\/[a-z]{2}(-[A-Z]{2})?(?=\/)/, '')

const getScrollTarget = (el: HTMLElement | null): HTMLElement => el ?? document.body

const getScrollTop = (el: HTMLElement): number =>
  el === document.body ? el.scrollTop || document.documentElement.scrollTop : el.scrollTop

/**
 * Save/restore scroll position for browser back/forward navigation only.
 * Supports both custom scroll containers (attach the returned ref) and
 * document.body (ignore the returned ref). `isReady` defers restore until
 * CSR data has loaded; retries until the element is tall enough.
 */
const useScrollPositionRestoration = <T extends HTMLElement = HTMLElement>(
  isReady: boolean = true
): RefObject<T> => {
  const router = useRouter()
  const ref = useRef<T>(null)

  // Persist scrollTop on every navigation away.
  useEffect(() => {
    const handleRouteChangeStart = () => {
      const el = getScrollTarget(ref.current)
      const scrollTop = getScrollTop(el)
      if (scrollTop > 0) {
        sessionStorage.setItem(`${KEY_PREFIX}${router.asPath}`, String(scrollTop))
      }
    }
    const handleRouteChangeError = () => {
      pendingPopStatePath = null
    }

    router.events.on('routeChangeStart', handleRouteChangeStart)
    router.events.on('routeChangeError', handleRouteChangeError)
    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart)
      router.events.off('routeChangeError', handleRouteChangeError)
    }
  }, [router])

  // Restore — only if this page was reached via back/forward AND the recorded
  // destination matches the current path. Guards against stale pendingPopStatePath
  // left over when forward-navigating to a page that does not use this hook.
  useEffect(() => {
    if (!isReady) return

    const key = `${KEY_PREFIX}${router.asPath}`
    const raw = sessionStorage.getItem(key)
    if (raw === null) return

    const isPopState =
      pendingPopStatePath !== null && stripLocalePrefix(pendingPopStatePath) === router.asPath

    if (!isPopState) {
      sessionStorage.removeItem(key)
      return
    }

    pendingPopStatePath = null
    sessionStorage.removeItem(key)

    const targetY = Number(raw)
    const MAX_RETRIES = 6
    let retries = 0
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const attempt = () => {
      const el = getScrollTarget(ref.current)
      el.scrollTop = targetY
      if (Math.abs(getScrollTop(el) - targetY) < 1 || retries >= MAX_RETRIES) return
      retries++
      timeoutId = setTimeout(attempt, 50 * retries) // 50ms → 100ms → 150ms → ...
    }

    requestAnimationFrame(attempt)
    return () => {
      if (timeoutId !== null) clearTimeout(timeoutId)
    }
  }, [isReady, router.asPath])

  return ref
}

export default useScrollPositionRestoration
